import { memo, useCallback, useEffect, useMemo } from 'react';

import size from '@/assets/theme/size';
import FlexItem from '@/components/atomic/atoms/layout/flex-item';
import GridContainer from '@/components/atomic/atoms/layout/grid-container';
import Navbar from '@/components/atomic/atoms/surfaces/navbar';
import HamburgerMenuList from '@/components/atomic/molecules/navigation/hamburger-menu-list';
import LogoHandler from '@/components/atomic/molecules/navigation/logo-handler';
import TopNavItems from '@/components/atomic/molecules/navigation/top-nav-items';
import ViewportRender from '@/components/atomic/molecules/utils/viewport/viewport-render';
import { NavmenuProps } from '@/components/atomic/templates/navigation/navmenu/typings';
import withExposer from '@/hocs/with-exposer';
import useFilteredRightXfPaths from '@/hooks/use-filtered-right-xf-paths/use-filtered-right-xf-paths.ts';
import useSavedBuilds from '@/hooks/use-saved-builds';
import { BasicComponentProps, NavigationItem } from '@/typings/generic';
import Bootstrap from '@/utils/bootstrap';
import SsrPlaceholderHelper from '@/utils/ssr-placeholder-helper';

const { laptop } = size;

const NavmenuComponent = ({
	leftXfPaths,
	rightXfPaths,
	mobileBurgerMenuLabel,
	mobileBurgerAriaLabel,
	locale,
	scsApiUrl,
	logos,
	$exposerSuffix,
}: NavmenuProps & BasicComponentProps) => {
	useSavedBuilds({ locale, scsApiUrl });

	const filteredRightXfPaths = useFilteredRightXfPaths(rightXfPaths);

	const canRender = useMemo(
		() =>
			(leftXfPaths && leftXfPaths.length > 0) ||
			(filteredRightXfPaths && filteredRightXfPaths.length > 0) ||
			logos,
		[leftXfPaths, logos, filteredRightXfPaths],
	);

	const generatePrefetchLinks = useCallback(
		(xfPaths: NavigationItem[] | undefined) => {
			if (xfPaths) {
				xfPaths.forEach((item) => {
					const modelPath = Bootstrap.generateModelPath(
						item.fragmentVariationPath,
					);
					if (modelPath && item.id) {
						const linkElementId = `prefetch-naas-${item.id}`;
						let linkElement =
							document.querySelector<HTMLLinkElement>(
								`#${linkElementId}`,
							);
						if (!linkElement) {
							linkElement = document.createElement('link');
							linkElement.id = linkElementId;
							linkElement.rel = 'prefetch';
							linkElement.as = 'fetch';
							linkElement.type = 'application/json';
							linkElement.href = `${modelPath}.model.json`;
							document.head.appendChild(linkElement);
						}
					}
				});
			}
		},
		[],
	);

	useEffect(() => {
		generatePrefetchLinks(leftXfPaths);
		generatePrefetchLinks(filteredRightXfPaths);
	}, [generatePrefetchLinks, leftXfPaths, filteredRightXfPaths]);

	useEffect(() => {
		if (canRender) SsrPlaceholderHelper.cleanUp();
	}, [canRender]);

	if (!canRender) return null;

	return (
		<Navbar $exposerSuffix={$exposerSuffix}>
			<GridContainer
				$gridTemplateColumns='1fr auto 1fr'
				$height='100%'
				$exposerSuffix={$exposerSuffix}
			>
				<FlexItem
					$flex={1}
					$breakpoints={{ [laptop]: { $paddingLeft: 20 } }}
					$exposerSuffix={$exposerSuffix}
				>
					<ViewportRender
						Component={HamburgerMenuList}
						componentProps={{
							mobileBurgerAriaLabel,
							mobileBurgerMenuLabel,
							$exposerSuffix,
						}}
						$breakpoints={{
							[laptop]: {
								Component: TopNavItems,
								componentProps: {
									$navigationItems: leftXfPaths ?? [],
									$variant: 'primary',
									$exposerSuffix,
								},
							},
						}}
					/>
				</FlexItem>

				{logos ? (
					<FlexItem $exposerSuffix={$exposerSuffix}>
						<LogoHandler
							logos={logos}
							$exposerSuffix={$exposerSuffix}
						/>
					</FlexItem>
				) : null}

				<FlexItem
					$flex={1}
					$justifyContent='flex-end'
					$exposerSuffix={$exposerSuffix}
				>
					<ViewportRender
						Component={TopNavItems}
						componentProps={{
							$navigationItems: (filteredRightXfPaths ?? [])
								.filter(
									({ showInNavbarOnMobileViewport }) =>
										showInNavbarOnMobileViewport,
								)
								.map((item) => ({
									...item,
									hasSeparator: false,
									label: '',
								})),
							$variant: 'utility',
							$exposerSuffix: $exposerSuffix,
						}}
						$breakpoints={{
							[laptop]: {
								Component: TopNavItems,
								componentProps: {
									$navigationItems: (
										filteredRightXfPaths ?? []
									).filter(
										({ hideNavMenuOnDesktop }) =>
											!hideNavMenuOnDesktop,
									),
									$variant: 'utility',
									$exposerSuffix,
								},
							},
						}}
					/>
				</FlexItem>
			</GridContainer>
		</Navbar>
	);
};

const Navmenu = memo(withExposer(NavmenuComponent, 'navmenu'));

export default Navmenu;
